// ─────────────────────────────────────────────────────────────────────────────
// Notifications (bell) data layer, 1:1 port of site/js/notifications-wiring.js.
//
// Server contract (worker: cntrl-auth-worker.js, verified verbatim):
//   GET  /api/notifications              → handleListNotifications      :1582-1598
//        return jsonResponse({ ok: true, notifications: rows.filter(..).map(r => ({
//          id, kind, title, body, route: r.route||null,
//          data: r.data_json ? JSON.parse(r.data_json) : null,
//          read: !!r.read_at, created_at }))});
//   GET  /api/notifications/unread-count → handleUnreadNotificationCount :1624
//        return jsonResponse({ ok: true, unread: unread });
//   POST /api/notifications/:id/read     → handleMarkNotificationRead    :1639  ({ ok: true })
//   POST /api/notifications/read-all     → handleMarkAllNotificationsRead :1777 ({ ok: true })
//   POST /api/notifications/:id/dismiss  → handleDismissNotification      :1795 ({ ok: true })
//   POST /api/notifications/dismiss-all  → handleDismissAllNotifications  :1811 ({ ok: true })
//   POST /api/roster/invite/:id/:action  → ({ ok: true, ... })  (accept | decline)
//
// The legacy GET sends authHeaders(false) (Bearer only) and POST sends
// authHeaders(true) (Bearer + Content-Type), apiGet/apiPost reproduce that.
// ─────────────────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useRef, useState } from 'react';
import { apiGet, apiPost } from './api';
import { getToken } from './auth';

const POLL_INTERVAL_MS = 30 * 1000;
const LIST_LIMIT = 20;

/** One notification row, exactly as the worker's list handler returns it. */
export interface AcNotification {
  readonly id: string | number;
  readonly kind: string;
  readonly title: string | null;
  readonly body: string | null;
  readonly route: string | null;
  /** Pre-parsed data_json blob (object) or null. */
  readonly data: Record<string, unknown> | null;
  read: boolean;
  readonly created_at: string;
}

interface ListResponse {
  readonly ok?: boolean;
  readonly notifications?: AcNotification[];
}
interface UnreadResponse {
  readonly ok?: boolean;
  readonly unread?: number | string;
}

// ─── Fetchers (mirror notifications-wiring.js fetchList/fetchUnreadCount/…) ────

/** GET /api/notifications → res.notifications (worker :1584). */
export async function fetchNotifications(): Promise<AcNotification[]> {
  if (!getToken()) return [];
  try {
    const res = await apiGet<ListResponse>('auth', '/api/notifications');
    return res && res.ok && Array.isArray(res.notifications) ? res.notifications : [];
  } catch {
    return [];
  }
}

/** GET /api/notifications/unread-count → res.unread (worker :1624). */
export async function fetchUnreadCount(): Promise<number> {
  if (!getToken()) return 0;
  try {
    const res = await apiGet<UnreadResponse>('auth', '/api/notifications/unread-count');
    if (!res || !res.ok) return 0;
    return parseInt(String(res.unread ?? 0), 10) || 0;
  } catch {
    return 0;
  }
}

/** POST /api/notifications/:id/read. */
export async function markNotificationRead(id: string | number): Promise<boolean> {
  if (!getToken() || id == null) return false;
  try {
    await apiPost('auth', '/api/notifications/' + encodeURIComponent(String(id)) + '/read');
    return true;
  } catch {
    return false;
  }
}

/** POST /api/notifications/read-all. */
export async function markAllNotificationsRead(): Promise<boolean> {
  if (!getToken()) return false;
  try {
    await apiPost('auth', '/api/notifications/read-all');
    return true;
  } catch {
    return false;
  }
}

/** POST /api/notifications/:id/dismiss (hard delete one row). */
export async function dismissNotification(id: string | number): Promise<boolean> {
  if (!getToken() || id == null) return false;
  try {
    await apiPost('auth', '/api/notifications/' + encodeURIComponent(String(id)) + '/dismiss');
    return true;
  } catch {
    return false;
  }
}

/** POST /api/notifications/dismiss-all (wipe all rows). */
export async function dismissAllNotifications(): Promise<boolean> {
  if (!getToken()) return false;
  try {
    await apiPost('auth', '/api/notifications/dismiss-all');
    return true;
  } catch {
    return false;
  }
}

/** POST /api/roster/invite/:membershipId/:action, action is 'accept'|'decline'. */
export async function respondToRosterInvite(
  membershipId: string | number,
  action: 'accept' | 'decline',
): Promise<boolean> {
  if (!getToken() || !membershipId || !action) return false;
  try {
    await apiPost(
      'auth',
      '/api/roster/invite/' + encodeURIComponent(String(membershipId)) + '/' + action,
    );
    return true;
  } catch {
    return false;
  }
}

// ─── Display helpers (verbatim from notifications-wiring.js) ───────────────────

/** Server stores SQLite "YYYY-MM-DD HH:MM:SS" UTC → "5m ago" / "Mar 3". */
export function timeAgo(serverTs?: string | null): string {
  if (!serverTs) return '';
  let iso = String(serverTs).replace(' ', 'T');
  if (iso.indexOf('Z') < 0 && iso.indexOf('+') < 0) iso = iso + 'Z';
  const ms = Date.parse(iso);
  if (!ms || isNaN(ms)) return '';
  const diff = Math.max(0, Date.now() - ms);
  const s = Math.floor(diff / 1000);
  if (s < 60) return s + 's ago';
  const m = Math.floor(s / 60);
  if (m < 60) return m + 'm ago';
  const h = Math.floor(m / 60);
  if (h < 24) return h + 'h ago';
  const d = Math.floor(h / 24);
  if (d < 7) return d + 'd ago';
  return new Date(ms).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** Server prepends "[Band] " for multi-band users, strip it for the drawer. */
export function stripBandPrefix(title?: string | null): string {
  if (!title) return '';
  return String(title).replace(/^\[[^\]]+\]\s*/, '');
}

/** notification.kind → human label (verbatim kindToLabel). */
export function kindToLabel(kind?: string): string {
  switch (kind) {
    case 'post_spike':
      return '↑ Post taking off';
    case 'playlist_add':
      return '♫ Playlist add';
    case 'friend_request':
      return 'Friend request';
    case 'show_opportunity':
      return 'Show invite';
    case 'tour_opportunity':
      return 'Tour invite';
    case 'flight_delay':
      return '✈ Flight delay';
    case 'dos_load_in':
      return 'Load-in';
    case 'dos_showtime':
      return 'Showtime';
    case 'message':
      return 'Message';
    case 'roster_invite':
      return 'Roster invitation';
    case 'roster_invite_accepted':
      return '✓ Invitation accepted';
    case 'roster_invite_declined':
      return '✕ Invitation declined';
    case 'opportunity_response':
      return '✓ Response';
    default:
      return String(kind || '').replace(/_/g, ' ');
  }
}

/** Best-effort read of a row's data blob (already an object from the worker). */
export function notifData(n: AcNotification | null): Record<string, unknown> | null {
  if (!n) return null;
  if (n.data && typeof n.data === 'object') return n.data;
  return null;
}

/** The membership_id carried on a roster_invite row (for Accept/Decline). */
export function inviteMembershipId(n: AcNotification): string | null {
  const d = notifData(n);
  const id = d && (d.membership_id as string | number | undefined);
  return id != null && id !== '' ? String(id) : null;
}

/* notif-routing 2026-07-19 (Ryan: "anytime someone gets a bell notification,
   when they click it it needs to take them to what the notification is"),
   the worker stamps routes in LEGACY MOBILE terms (/m/thread.html?id=…).
   Each surface translates to its own navigation:
     • resolveNotifDest      → desktop (react-router path, or the messages
                               overlay via the ac:open-messages event)
     • notifMobileRoute      → the m-react HashRouter path
   Every stored route shape is enumerated here; unknown shapes land on the
   dashboard instead of a dead hash. */
export type NotifDest =
  | { readonly type: 'path'; readonly path: string }
  | { readonly type: 'inbox' }
  | { readonly type: 'thread'; readonly threadId: string };

function routeParam(route: string, key: string): string {
  try {
    return new URLSearchParams((route.split('?')[1] || '').split('#')[0]).get(key) || '';
  } catch { return ''; }
}

/** Desktop destination for a stored notification route. */
export function resolveNotifDest(route?: string | null): NotifDest | null {
  if (!route) return null;
  const r = String(route);
  if (r.indexOf('/m/thread.html') === 0) {
    const tid = routeParam(r, 'id');
    return tid ? { type: 'thread', threadId: tid } : { type: 'inbox' };
  }
  if (r.indexOf('/m/inbox.html') === 0) return { type: 'inbox' };
  if (r.indexOf('/m/contacts.html') === 0) return { type: 'path', path: '/contacts' };
  if (r.indexOf('/m/show-logistics.html') === 0 || r.indexOf('/m/day-of-show.html') === 0) {
    return { type: 'path', path: '/schedule' };
  }
  if (r.indexOf('/m/settings.html') === 0 || r === '/app#settings') return { type: 'path', path: '/settings' };
  if (r.indexOf('/m/dashboard.html') === 0 || r === '/app') return { type: 'path', path: '/' };
  /* Desktop-native routes stored as-is: /content?…, /roster?invite=… */
  if (r.charAt(0) === '/' && r.indexOf('/m/') !== 0 && r.indexOf('/app') !== 0) {
    return { type: 'path', path: r };
  }
  return { type: 'path', path: '/' };
}

/** m-react HashRouter path for a stored notification route. */
export function notifMobileRoute(route?: string | null): string {
  if (!route) return '';
  const r = String(route);
  const legacy = /^\/m\/([a-z-]+)\.html(\?[^#]*)?(#.*)?$/.exec(r);
  if (legacy) {
    const page = legacy[1];
    if (page === 'dashboard') return '/';
    /* thread/inbox/contacts/settings/show-logistics/day-of-show all exist as
       m-react routes; MobileThread accepts ?id= so the query rides along. */
    return '/' + page + (legacy[2] || '') + (legacy[3] || '');
  }
  if (r === '/app#settings') return '/settings';
  if (r === '/app') return '/';
  if (r.indexOf('/content') === 0) return r;
  if (r.indexOf('/roster') === 0) return '/'; /* no roster surface on mobile */
  const bare = notifRouteHash(r);
  return bare ? '/' + bare : '';
}

/** Routes are stored as "/content?…"; desktop hash-routes → strip leading "/". */
export function notifRouteHash(route?: string | null): string {
  if (!route) return '';
  let r = String(route);
  if (r.charAt(0) === '/') r = r.slice(1);
  if (r.charAt(0) === '#') r = r.slice(1);
  return r;
}

// ─── Hook: useNotifications, owns items/unread/open + the poll loop ───────────

export interface NotificationsApi {
  readonly items: AcNotification[];
  readonly unread: number;
  readonly open: boolean;
  setOpen(next: boolean): void;
  /** Pull fresh data now (unread count always; list if open or empty). */
  refresh(): Promise<void>;
  markRead(id: string | number): void;
  markAll(): void;
  dismiss(id: string | number): void;
  clearAll(): void;
  respondInvite(n: AcNotification, action: 'accept' | 'decline'): Promise<void>;
}

/* Message/DM notifications belong on the MESSAGE icon, not the bell, the
   messages overlay counts + surfaces them separately. Filter them out of the
   bell so a band DM never lights or fills the notifications drawer. */
function isMessageKind(kind?: string): boolean {
  const k = String(kind || '');
  return k === 'message' || k.startsWith('message_') || k.startsWith('band_message') || k.startsWith('dm');
}

export function useNotifications(): NotificationsApi {
  const [items, setItems] = useState<AcNotification[]>([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);

  // Refs so the polling closure always sees current open/items without
  // re-subscribing the interval each render (mirrors the IIFE's `state`).
  const openRef = useRef(open);
  const itemsLenRef = useRef(items.length);
  useEffect(() => {
    openRef.current = open;
  }, [open]);
  useEffect(() => {
    itemsLenRef.current = items.length;
  }, [items.length]);

  const refresh = useCallback(async () => {
    // Pull the list and strip message/DM kinds, those are the message
    // icon's job. Derive the bell's unread from what remains so a band DM
    // neither fills the drawer nor lights the dot. (Also drops the bell's
    // dependency on /unread-count, which was erroring intermittently.)
    const list = (await fetchNotifications()).filter((n) => !isMessageKind(n.kind));
    setItems(list.slice(0, LIST_LIMIT));
    setUnread(list.filter((n) => !n.read).length);
  }, []);

  // Poll loop: 30s while visible, paused on hidden tabs, immediate on
  // visibilitychange → visible. Mirrors startPolling() + the visibility hook.
  useEffect(() => {
    void refresh();
    const timer = window.setInterval(() => {
      if (document.hidden) return;
      void refresh();
    }, POLL_INTERVAL_MS);
    const onVis = (): void => {
      if (!document.hidden) void refresh();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [refresh]);

  // Opening the drawer pulls a fresh list immediately (open() :463).
  const setOpenSync = useCallback(
    (next: boolean) => {
      setOpen(next);
      if (next) void refresh();
    },
    [refresh],
  );

  const markRead = useCallback((id: string | number) => {
    setItems((prev) => {
      let wasUnread = false;
      const next = prev.map((n) => {
        if (n.id === id && !n.read) {
          wasUnread = true;
          return { ...n, read: true };
        }
        return n;
      });
      if (wasUnread) setUnread((u) => Math.max(0, u - 1));
      return next;
    });
    void markNotificationRead(id);
  }, []);

  const markAll = useCallback(() => {
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnread(0);
    void markAllNotificationsRead();
  }, []);

  const dismiss = useCallback((id: string | number) => {
    setItems((prev) => {
      const target = prev.find((n) => n.id === id);
      if (target && !target.read) setUnread((u) => Math.max(0, u - 1));
      return prev.filter((n) => n.id !== id);
    });
    void dismissNotification(id);
  }, []);

  const clearAll = useCallback(() => {
    setItems([]);
    setUnread(0);
    void dismissAllNotifications();
  }, []);

  const respondInvite = useCallback(
    async (n: AcNotification, action: 'accept' | 'decline') => {
      const membershipId = inviteMembershipId(n);
      if (!membershipId) return;
      const ok = await respondToRosterInvite(membershipId, action);
      if (!ok) return;
      // Acting on the invite implicitly reads + removes it (onInviteAction :391).
      void markNotificationRead(n.id);
      setItems((prev) => prev.filter((x) => x.id !== n.id));
      setUnread((u) => Math.max(0, u - 1));
      // After accept, the legacy code reloads so the new band membership shows.
      if (action === 'accept') {
        window.setTimeout(() => window.location.reload(), 600);
      }
    },
    [],
  );

  return {
    items,
    unread,
    open,
    setOpen: setOpenSync,
    refresh,
    markRead,
    markAll,
    dismiss,
    clearAll,
    respondInvite,
  };
}
